/**
 * «Моё проживание» (canon design-guest v7 §7, UC-G) — гостевой раздел.
 *
 * StayCard по дизайну §7: № номера + тип номера + имя гостя крупно в шапке,
 * период заезд→выезд с временем, статус. Действия (ghost-кнопки, каждая =
 * запрос/заявка):
 *   • Продлить проживание   → POST /v1/stays/{id}/extensions (manager approval)
 *   • Поздний выезд         → POST /v1/stays/{id}/late-checkout
 *   • Детали номера         → drawer §3 (тип/удобства/фото)
 *   • Мой счёт              → /folio (фолио гостя, отдельный модуль #338)
 *
 * Данные: GET /v1/me/stays (ownership на бэке) + GET /v1/rooms/{id} (№ номера,
 * тип) через useRoomDetail + actor.name (GET /v1/platform/me) для имени гостя.
 * Полей, которых нет на бэке (точная вместимость проживания), НЕ выдумываем.
 * Если проживаний нет — пустое состояние. Полный i18n, мобайл 375 (одна
 * колонка), 768+ split.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { StayResponse, StayState } from '@hsc/types';
import { STAY_STATE_LABEL, STAY_STATE_TONE, formatDate } from '../../lib/fleet-format.js';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { Toast } from '../../shared/ui/Toast.js';
import { RoomDetailDrawer } from './RoomDetailDrawer.js';
import { ExtendStayDialog } from './ExtendStayDialog.js';
import { LateCheckoutDialog } from './LateCheckoutDialog.js';
import { useMyStays, useRoomDetail, useSelfCheckout } from './useGuestStay.js';

/** Сортировка: текущее/ближайшее проживание первым (живые статусы выше). */
const ACTIVE_RANK: Readonly<Record<string, number>> = {
  checked_in: 0,
  confirmed: 1,
  pending: 2,
  checked_out: 3,
  cancelled: 4,
  no_show: 5,
};

function pickPrimary(stays: StayResponse[]): StayResponse | undefined {
  return [...stays].sort((a, b) => {
    const ra = ACTIVE_RANK[a.state] ?? 9;
    const rb = ACTIVE_RANK[b.state] ?? 9;
    if (ra !== rb) return ra - rb;
    return a.from < b.from ? -1 : 1;
  })[0];
}

/** «15.06 · 14:00» — дата + время заезда/выезда (design §7). */
function formatDateTimeShort(iso: string | undefined): string {
  if (iso === undefined || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  // Если время «полночь» (бэк отдал только дату) — не показываем 00:00.
  return time === '00:00' ? formatDate(iso) : `${date} · ${time}`;
}

type Dialog = 'extend' | 'late' | 'room' | null;

export function MyStayPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMyStays();
  const me = usePlatformMe();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [confirmCheckout, setConfirmCheckout] = useState(false);
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);
  const selfCheckout = useSelfCheckout();

  const primary = useMemo(() => pickPrimary(data ?? []), [data]);
  const room = useRoomDetail(primary?.room_id ?? null);
  const canRequest =
    primary !== undefined &&
    (primary.state === 'confirmed' || primary.state === 'checked_in');
  // «Выселиться» доступно только для активного (заехавшего) проживания.
  const canCheckout = primary !== undefined && primary.state === 'checked_in';

  const roomNumber = room.data?.room.number;
  const roomTypeName =
    room.data?.type?.name !== undefined && room.data.type.name.trim() !== ''
      ? room.data.type.name
      : undefined;
  const guestName =
    me.data?.actor.name !== undefined && me.data.actor.name.trim() !== ''
      ? me.data.actor.name
      : undefined;

  // Действия гостя (детали номера / продление / поздний выезд) открываются
  // ОТДЕЛЬНЫМ ЭКРАНОМ — карточка проживания не видна, «Назад» возвращает
  // ([[feedback_every_form_is_a_separate_screen]]).
  if (dialog === 'room' && primary) {
    return (
      <RoomDetailDrawer
        roomId={primary.room_id}
        roomNumber={roomNumber}
        onClose={() => setDialog(null)}
      />
    );
  }
  if (dialog === 'extend' && primary) {
    return (
      <ExtendStayDialog
        stay={primary}
        onClose={() => setDialog(null)}
        onDone={(ok) => {
          setDialog(null);
          setToast(
            ok
              ? { msg: 'Запрос на продление отправлен — менеджер свяжется с вами.', tone: 'info' }
              : { msg: 'Не удалось отправить запрос. Попробуйте ещё раз.', tone: 'error' },
          );
        }}
      />
    );
  }
  if (dialog === 'late' && primary) {
    return (
      <LateCheckoutDialog
        stay={primary}
        onClose={() => setDialog(null)}
        onDone={(ok) => {
          setDialog(null);
          setToast(
            ok
              ? { msg: 'Запрос позднего выезда отправлен — ждите подтверждения.', tone: 'info' }
              : { msg: 'Не удалось отправить запрос. Попробуйте ещё раз.', tone: 'error' },
          );
        }}
      />
    );
  }

  return (
    <section data-testid="my-stay-page" className="my-stay">
      <h1 className="hsc-page__title">Моё проживание</h1>

      {isLoading ? (
        <Card>
          <Skeleton height={28} radius={8} />
          <Skeleton height={20} radius={8} />
          <Skeleton height={20} radius={8} />
        </Card>
      ) : isError ? (
        <Card>
          <p role="alert">Не удалось загрузить проживание. Попробуйте обновить страницу.</p>
        </Card>
      ) : !primary ? (
        <Card>
          <p data-testid="my-stay-empty">
            Активного проживания пока нет. Когда отель подтвердит вашу бронь, она появится здесь.
          </p>
          <div className="rq-actions" style={{ marginTop: 12 }}>
            <Button variant="secondary" onClick={() => navigate('/services')}>
              Каталог услуг
            </Button>
          </div>
        </Card>
      ) : (
        <div className="my-stay__grid">
          {/* Stay-card §7: № номера + тип + гость крупно, период с временем */}
          <Card>
            <div className="my-stay__card" data-testid="my-stay-card">
              <div className="my-stay__top">
                <div className="my-stay__id">
                  <span className="my-stay__roomno" data-testid="my-stay-room-no">
                    {roomNumber !== undefined ? `№ ${roomNumber}` : 'Ваш номер'}
                  </span>
                  {roomTypeName !== undefined && (
                    <span className="my-stay__roomtype" data-testid="my-stay-room-type">
                      {roomTypeName}
                    </span>
                  )}
                  {guestName !== undefined && (
                    <span className="my-stay__guest" data-testid="my-stay-guest">
                      {guestName}
                    </span>
                  )}
                </div>
                <span
                  className={`fleet-badge fleet-badge--${STAY_STATE_TONE[primary.state as StayState] ?? 'muted'}`}
                  data-testid="my-stay-status"
                >
                  {STAY_STATE_LABEL[primary.state as StayState] ?? primary.state}
                </span>
              </div>

              <dl className="my-stay__dates">
                <div>
                  <dt>Заезд</dt>
                  <dd data-testid="my-stay-from">{formatDateTimeShort(primary.from)}</dd>
                </div>
                <span className="my-stay__arrow" aria-hidden="true">
                  →
                </span>
                <div>
                  <dt>Выезд</dt>
                  <dd data-testid="my-stay-to">{formatDateTimeShort(primary.to)}</dd>
                </div>
              </dl>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDialog('room')}
                data-testid="my-stay-room-detail"
              >
                Детали номера →
              </Button>
            </div>
          </Card>

          {/* Действия */}
          <Card>
            <h2 className="hsc-card__title">Действия</h2>
            <div className="hsc-stack" data-testid="my-stay-actions">
              <Button
                variant="secondary"
                onClick={() => setDialog('extend')}
                disabled={!canRequest}
                data-testid="my-stay-extend"
              >
                Продлить проживание
              </Button>
              <Button
                variant="secondary"
                onClick={() => setDialog('late')}
                disabled={!canRequest}
                data-testid="my-stay-late"
              >
                Поздний выезд
              </Button>
              <Button
                variant="secondary"
                onClick={() => setConfirmCheckout(true)}
                disabled={!canCheckout || selfCheckout.isPending}
                data-testid="my-stay-checkout"
              >
                Выселиться
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/folio')}
                data-testid="my-stay-folio"
              >
                Мой счёт →
              </Button>
            </div>

            {confirmCheckout && primary && (
              <div className="hsc-confirm" role="dialog" data-testid="my-stay-checkout-confirm">
                <p>Подтвердить выселение? Номер будет освобождён.</p>
                <div className="rq-actions">
                  <Button
                    size="sm"
                    disabled={selfCheckout.isPending}
                    data-testid="my-stay-checkout-yes"
                    onClick={() =>
                      selfCheckout.mutate(
                        { stayId: primary.id },
                        {
                          onSuccess: () => {
                            setConfirmCheckout(false);
                            setToast({ msg: 'Вы выселились. Спасибо, что выбрали нас!', tone: 'info' });
                          },
                          onError: () => {
                            setConfirmCheckout(false);
                            setToast({ msg: 'Не удалось выселиться. Обратитесь на ресепшен.', tone: 'error' });
                          },
                        },
                      )
                    }
                  >
                    Да, выселиться
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirmCheckout(false)}
                    data-testid="my-stay-checkout-no"
                  >
                    Нет
                  </Button>
                </div>
              </div>
            )}
            {!canRequest && (
              <p className="ta-muted" style={{ marginTop: 8 }}>
                Продление и поздний выезд доступны для подтверждённого проживания.
              </p>
            )}
            <p className="ta-muted" style={{ marginTop: 8 }}>
              Оплата при выезде на ресепшене — мы не списываем деньги онлайн.
            </p>
          </Card>
        </div>
      )}

      {toast && (
        <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />
      )}
    </section>
  );
}
